import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { LayoutGrid, Clock, Type, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ZoneConfig = ({ pageId }) => {
  const [zoneEnabled, setZoneEnabled] = useState(false);
  const [showClock, setShowClock] = useState(true);
  const [tickerText, setTickerText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/pages/${pageId}`);
        setZoneEnabled(response.data.zone_layout || false);
        setShowClock(response.data.zone_clock !== false);
        setTickerText(response.data.zone_ticker || '');
      } catch (error) {
        console.error('Failed to load zone config:', error);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [pageId]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, {
        zone_layout: zoneEnabled,
        zone_clock: showClock,
        zone_ticker: tickerText
      });
      toast.success('Zone layout settings saved');
    } catch (error) {
      toast.error('Failed to save zone settings');
    } finally {
      setSaving(false);
    }
  };

  const toggleZone = async (enabled) => {
    setZoneEnabled(enabled);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { zone_layout: enabled });
      toast.success(enabled ? 'Multi-zone layout enabled' : 'Multi-zone layout disabled');
    } catch (error) {
      setZoneEnabled(!enabled);
      toast.error('Failed to toggle zone layout');
    }
  };

  if (loading) return null;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="zone-config">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold flex items-center gap-2">
              <LayoutGrid className="w-5 h-5 text-cyan-400" /> Multi-Zone Layout
            </h3>
            <p className="text-slate-400 text-sm mt-1">
              Split the screen: slideshow + clock sidebar + scrolling ticker
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`text-sm ${zoneEnabled ? 'text-cyan-400' : 'text-slate-500'}`}>
              {zoneEnabled ? 'On' : 'Off'}
            </span>
            <Switch
              checked={zoneEnabled}
              onCheckedChange={toggleZone}
              data-testid="zone-toggle"
            />
          </div>
        </div>

        {zoneEnabled && (
          <div className="space-y-4 mt-4 pt-4 border-t border-slate-800">
            {/* Clock toggle */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-slate-400" />
                <span className="text-slate-300 text-sm">Show Clock & Date Sidebar</span>
              </div>
              <Switch
                checked={showClock}
                onCheckedChange={setShowClock}
                data-testid="zone-clock-toggle"
              />
            </div>

            {/* Ticker text */}
            <div>
              <label className="flex items-center gap-2 text-slate-300 text-sm mb-2">
                <Type className="w-4 h-4 text-slate-400" /> Bottom Ticker Text
              </label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={tickerText}
                  onChange={(e) => setTickerText(e.target.value)}
                  placeholder="e.g., Welcome to our facility! Wi-Fi: Guest123"
                  className="bg-slate-950 border-slate-700 text-white flex-1"
                  data-testid="zone-ticker-input"
                />
              </div>
            </div>

            <Button
              onClick={saveConfig}
              disabled={saving}
              className="bg-cyan-500 hover:bg-cyan-600 w-full"
              data-testid="zone-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Zone Settings
            </Button>

            {/* Mini preview */}
            <div className="bg-slate-950 rounded-lg p-3 border border-slate-800">
              <p className="text-slate-500 text-xs mb-2">Layout Preview:</p>
              <div className="flex gap-1 h-20">
                <div className="flex-1 bg-slate-800 rounded flex items-center justify-center">
                  <span className="text-slate-500 text-xs">Slideshow</span>
                </div>
                {showClock && (
                  <div className="w-12 bg-slate-800 rounded flex items-center justify-center">
                    <span className="text-slate-500 text-[10px]">Clock</span>
                  </div>
                )}
              </div>
              {tickerText && (
                <div className="mt-1 h-4 bg-slate-800 rounded flex items-center justify-center">
                  <span className="text-slate-500 text-[10px]">Ticker</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ZoneConfig;
